import {type Outcome, type Rec, nextRec, RT_WINDOW} from './learner-model';

/**
 * 学習の記録の保存。localStorage を直接触るのはこのファイルだけ。
 *
 * 設計の要点:
 * - ストレージは注入できる（Node のテストには localStorage が無い）。無ければメモリのみ。
 * - 起動時に 1 回だけ書き込みを試し、throw する環境（Safari のプライベートモード等）では
 *   以後 localStorage に一切触らない。毎回 throw させない。
 * - 読み込みは疑ってかかる。壊れた JSON・未知の版・不正なレコードで落ちない。
 *   不正なレコードはその 1 件だけ捨てて残りを使う。
 * - 書き込みは回答のたびではなく 2 秒のトレーリングスロットル。直列化結果が前回と
 *   同じなら書かない。ページが隠れるとき・切断時は即時フラッシュ。
 * - 容量超過は 1 回だけ剪定して再試行し、それでも駄目ならメモリのみに落ちる。
 *   出題はどの段階でも動き続ける。
 */

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export const STORAGE_KEY = 'devanagari-quiz:progress';
const PROBE_KEY = 'devanagari-quiz:probe';
const SCHEMA_VERSION = 1;
/** 学習した暦日を何日ぶん持つか（直近 7 日の点列と、剪定の対象） */
export const DAYS_KEPT = 30;
/** 書き込みのスロットル */
export const WRITE_DELAY_MS = 2000;
export const GOALS = [10, 20, 40] as const;
export type Goal = (typeof GOALS)[number];
const DEFAULT_GOAL: Goal = 20;
/** 容量超過時に剪定する定着レコードの「最終回答からの経過日数」と最大件数 */
const PRUNE_AGE_DAYS = 60;
const PRUNE_MAX = 200;
/**
 * 保存する文字種の id。コントローラの CategoryKey（`${id}Checked`）と 1:1。
 * ここに無い値は読み込み時に捨てる。並びは正規化に使う。
 * **この文字列はディスク上のデータ**なので、改名するときは読み込み側に移行を書くこと
 * （ビットマスクや添字にしないのは、CATEGORIES の並びが変わりうるため）
 */
export const SETTING_KEYS = [
  'vowels',
  'consonants',
  'dependentVowels',
  'virama',
  'ayogavaha',
  'conjuncts',
  'numerals',
] as const;
export type SettingKey = (typeof SETTING_KEYS)[number];
/** 文字数の保存上限。スライダーの上限は文字種の合計で決まるので、ここでは型と範囲だけ見る */
const COUNT_MAX = 100_000;

/**
 * 保存する設定。2 つのフィールドは独立に検証し、片方が壊れていてももう片方は生かす。
 * 空の文字種は「何も選んでいない」ではなく「未保存」として扱う（全部外して離れた人を、
 * 次回また空の箱で迎えない。直前の有効な選択が残る）
 */
export interface QuizSettings {
  /** 練習する文字種（SETTING_KEYS の並びに正規化済み・1 つ以上） */
  sel?: readonly SettingKey[];
  /** 利用者が意図した文字数（上限でクランプする前の生の値） */
  cnt?: number;
  /**
   * 読み方から字を選ぶ向きを混ぜるか。既定（混ぜる）は書かず、切ったときだけ false を持つ
   * （設定を触っていない利用者のペイロードを変えないため）
   */
  rev?: false;
}

interface ProgressData {
  v: number;
  /** 今日の目標問題数 */
  goal: Goal;
  /**
   * 練習する文字種と文字数。学習の記録ではなく設定なので、目標と同じく「保存しない」の
   * 印にも残し、消去でも消えない。未保存なら null（コントローラの既定 母音 / 8）。
   * 未知の新しい版（readOnly）からは復元しない（別の意味に変わっている可能性がある）。
   * 再訪のたびに母音 8 字に戻るのが、記録を付け始めた学習者の最大の離脱要因だった
   */
  set: QuizSettings | null;
  /** 「このブラウザに保存する」を切った印。true のときはこのフラグだけを書く */
  off: boolean;
  /**
   * 消去の世代。clear() / undoClear() のたびに +1 する単調な番号。
   * 取り込み（merge）は「足す」方向にしか働かないので、これが無いと別のタブや
   * 同じページの 2 個目の要素が、消したはずの記録を次の書き込みで戻してしまう。
   * 世代の新しいほうの記録が全面的に勝つ。
   */
  cl: number;
  /** 学習した暦日（直近 DAYS_KEPT 件）。減らない累計は nd */
  days: number[];
  nd: number;
  /** 今日の出題数と正解数。d が今日でなければ読み込み時に 0 に戻る */
  today: {d: number; n: number; c: number};
  /** 計測できた正解の反応時間（直近 RT_WINDOW 件）。速い正解の閾値の個人適応に使う */
  rt: number[];
  /** 文字 id → レコード */
  r: Record<string, Rec>;
}

function emptyData(today: number): ProgressData {
  return {
    v: SCHEMA_VERSION,
    goal: DEFAULT_GOAL,
    off: false,
    set: null,
    cl: 0,
    days: [],
    nd: 0,
    today: {d: today, n: 0, c: 0},
    rt: [],
    r: {},
  };
}

/**
 * localStorage を探して、書けることまで確認する。
 * プロパティアクセス自体が SecurityError を投げる環境（iframe のストレージ分割等）も
 * ここで吸収する。
 */
export function detectStorage(): StorageLike | null {
  // localStorage はブラウザの機能。Node 22 以降は globalThis.localStorage を
  // 「触ると警告を出す空のオブジェクト」として持っているので、window の無い環境では見にいかない
  if (typeof window === 'undefined') return null;
  try {
    const s = (globalThis as {localStorage?: StorageLike}).localStorage;
    if (!s) return null;
    s.setItem(PROBE_KEY, '1');
    s.removeItem(PROBE_KEY);
    return s;
  } catch {
    return null;
  }
}

const isInt = (x: unknown): x is number => typeof x === 'number' && Number.isInteger(x);

/** レコード 1 件の検証。範囲外・型違い・件数違いは捨てる */
export function sanitizeRec(x: unknown): Rec | null {
  if (!Array.isArray(x) || x.length !== 5) return null;
  const [b, day, n, w, k] = x;
  if (![b, day, n, w, k].every(isInt)) return null;
  if (b < 1 || b > 5) return null;
  if (day < 0 || day > 100_000) return null;
  if (n < 1 || w < 0 || w > n || k < 1 || k > n) return null;
  return [b, day, n, w, k];
}

/**
 * 設定の検証。フィールドごとに独立に見る。未知の文字種は捨てて正規の並びに直し、
 * 1 つも残らなければ sel は無し。文字数は整数 2..COUNT_MAX のときだけ。反転は false のときだけ
 * （true や型違いは既定と同じ「無し」）。どれも残らなければ「未保存」（null）
 */
export function sanitizeSettings(x: unknown): QuizSettings | null {
  if (typeof x !== 'object' || x === null || Array.isArray(x)) return null;
  const o = x as Record<string, unknown>;
  const out: QuizSettings = {};
  if (Array.isArray(o.sel)) {
    const wanted = new Set(o.sel.filter((k): k is SettingKey => typeof k === 'string'));
    const sel = SETTING_KEYS.filter((k) => wanted.has(k));
    if (sel.length > 0) out.sel = sel;
  }
  if (isInt(o.cnt) && o.cnt >= 2 && o.cnt <= COUNT_MAX) out.cnt = o.cnt;
  if (o.rev === false) out.rev = false;
  return out.sel === undefined && out.cnt === undefined && out.rev === undefined ? null : out;
}

function sameSettings(a: QuizSettings | null, b: QuizSettings | null): boolean {
  if (!a || !b) return a === b;
  if (a.cnt !== b.cnt || a.rev !== b.rev) return false;
  const x = a.sel ?? [];
  const y = b.sel ?? [];
  return x.length === y.length && x.every((k, i) => k === y[i]);
}

/** 直列化した JSON を読み戻す。返り値の readOnly は「未知の新しい版」を意味する */
export function sanitize(raw: string | null, today: number): {data: ProgressData; readOnly: boolean} {
  const data = emptyData(today);
  if (raw === null || raw === '') return {data, readOnly: false};

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return {data, readOnly: false};
  }
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    return {data, readOnly: false};
  }
  const obj = parsed as Record<string, unknown>;
  if (!isInt(obj.v)) return {data, readOnly: false};
  // 別タブの新しいビルドが書いたデータを、古いこのビルドが潰してはいけない
  if (obj.v > SCHEMA_VERSION) return {data, readOnly: true};
  // v1 が初版なので移行表は空。版が増えたらここで順に適用する

  if (obj.off === true) data.off = true;
  if ((GOALS as readonly number[]).includes(obj.goal as number)) data.goal = obj.goal as Goal;
  data.set = sanitizeSettings(obj.set);
  if (isInt(obj.cl) && obj.cl >= 0) data.cl = obj.cl;
  if (Array.isArray(obj.days)) {
    data.days = obj.days.filter((d): d is number => isInt(d) && d >= 0).slice(-DAYS_KEPT);
  }
  data.nd = isInt(obj.nd) && obj.nd >= 0 ? Math.max(obj.nd, data.days.length) : data.days.length;
  const t = obj.today as Record<string, unknown> | undefined;
  if (t && typeof t === 'object' && isInt(t.d) && t.d === today && isInt(t.n) && isInt(t.c)) {
    data.today = {d: today, n: Math.max(0, t.n), c: Math.max(0, Math.min(t.n, t.c))};
  }
  if (Array.isArray(obj.rt)) {
    data.rt = obj.rt
      .filter((x): x is number => typeof x === 'number' && Number.isFinite(x) && x >= 0)
      .slice(-RT_WINDOW);
  }
  if (typeof obj.r === 'object' && obj.r !== null && !Array.isArray(obj.r)) {
    for (const [id, rec] of Object.entries(obj.r as Record<string, unknown>)) {
      const ok = sanitizeRec(rec);
      if (ok) data.r[id] = ok;
    }
  }
  return {data, readOnly: false};
}

/**
 * 別のタブ・同じページの 2 個目の要素が書いた値を取り込む。
 *
 * ストアは要素ごとに 1 個で、書き込みは全体置換なので、何もしないと後に書いたほうが
 * 相手の回答を全部消し、相手が切った「保存しない」まで解いてしまう。
 *
 * - 消去の世代 cl が違えば、新しいほうの記録が全面的に勝つ（消去と取り消しを伝播させる）
 * - 同じ世代なら id ごとに合成する。箱と最終回答日は新しいほう、回答数・誤答数・学習日数は
 *   大きいほう（配列ごと採ると、取り込みが遅れた側の 1 回答で 12 回の履歴が潰れる）
 * - 学習日は和集合、今日のカウンタは同日なら大きいほう、反応時間は件数の多いほう
 * - 「保存しない」と目標は、このセッションで自分が触っていなければストレージの現在値に従う。
 *   OR にすると一度取り込んだ off が二度と戻らず、相手が保存を再開しても消し続ける
 */
function mergeData(
  mine: ProgressData,
  theirs: ProgressData,
  touched: {off: boolean; goal: boolean; set: boolean},
  /** 前回の書き込み以降に自分が答えた id。相手の消去を採るときもこれだけは失わない */
  dirty: ReadonlySet<string>
): ProgressData {
  const goal = touched.goal ? mine.goal : theirs.goal;
  const off = touched.off ? mine.off : theirs.off;
  // 文字種と文字数も目標と同じ（2 つで 1 組として後勝ち）。相手が未保存（古いビルド等）なら自分の値を残す
  const set = touched.set ? mine.set : (theirs.set ?? mine.set);
  if (theirs.cl > mine.cl) {
    const r = {...theirs.r};
    for (const id of dirty) if (mine.r[id]) r[id] = mine.r[id];
    return {...theirs, goal, off, set, r};
  }
  if (mine.cl > theirs.cl) return {...mine, goal, off, set};

  const r: Record<string, Rec> = {...theirs.r};
  for (const [id, m] of Object.entries(mine.r)) {
    const t = r[id];
    if (!t) {
      r[id] = m;
      continue;
    }
    const newer = m[1] > t[1] || (m[1] === t[1] && m[2] >= t[2]) ? m : t;
    r[id] = [newer[0], newer[1], Math.max(m[2], t[2]), Math.max(m[3], t[3]), Math.max(m[4], t[4])];
  }
  const days = [...new Set([...theirs.days, ...mine.days])].sort((a, b) => a - b).slice(-DAYS_KEPT);
  const today =
    mine.today.d === theirs.today.d
      ? {
          d: mine.today.d,
          n: Math.max(mine.today.n, theirs.today.n),
          c: Math.max(mine.today.c, theirs.today.c),
        }
      : mine.today.d > theirs.today.d
        ? mine.today
        : theirs.today;
  return {
    v: SCHEMA_VERSION,
    goal,
    off,
    set,
    cl: mine.cl,
    days,
    nd: Math.max(mine.nd, theirs.nd, days.length),
    today,
    rt: mine.rt.length >= theirs.rt.length ? mine.rt : theirs.rt,
    r,
  };
}

export interface RecordResult {
  before: Rec | undefined;
  after: Rec;
  /** この回答で今日の目標に到達した */
  goalReached: boolean;
}

export class ProgressStore {
  #storage: StorageLike | null;
  #today: () => number;
  #data: ProgressData;
  /** 未知の新しい版を読んだ。読みも書きもしない */
  #readOnly = false;
  /** false になったら以後 setItem を呼ばない（容量超過で剪定しても駄目だったとき） */
  #writable: boolean;
  #dirty = false;
  #timer?: ReturnType<typeof setTimeout>;
  #lastWritten: string | null = null;
  /** 消去の取り消し用に、直前の直列化結果を保持する */
  #undo: string | null = null;
  /** このセッションで自分で切り替えた設定。別タブの値より自分の操作を優先する */
  #touched = {off: false, goal: false, set: false};
  /** 前回ストレージに書けてから答えた id。相手の消去を取り込むときも、これは捨てない */
  #dirtyIds = new Set<string>();

  constructor(opts: {storage?: StorageLike | null; today: () => number}) {
    this.#storage = opts.storage ?? null;
    this.#today = opts.today;
    let raw: string | null = null;
    if (this.#storage) {
      try {
        raw = this.#storage.getItem(STORAGE_KEY);
      } catch {
        this.#storage = null;
      }
    }
    const {data, readOnly} = sanitize(raw, this.#today());
    this.#data = data;
    this.#readOnly = readOnly;
    this.#writable = this.#storage !== null && !readOnly;
    this.#lastWritten = raw;
  }

  /** この環境で記録を保存できる（プライベートモード・容量超過で落ちていない） */
  get available(): boolean {
    return this.#writable;
  }

  get readOnly(): boolean {
    return this.#readOnly;
  }

  /** 「このブラウザに保存する」の状態 */
  get enabled(): boolean {
    return !this.#data.off;
  }

  /** レコードが 1 件も無い。出題側はこのとき旧コードパスに短絡する */
  get isEmpty(): boolean {
    for (const _ in this.#data.r) return false;
    return true;
  }

  get goal(): Goal {
    return this.#data.goal;
  }

  /** 保存された文字種と文字数。未保存なら null（未知の新しい版のときも null） */
  get settings(): QuizSettings | null {
    return this.#data.set;
  }

  get todayCount(): number {
    return this.#rollDay().n;
  }

  get todayCorrect(): number {
    return this.#rollDay().c;
  }

  /** 学習した暦日（古い順、直近 DAYS_KEPT 件） */
  get days(): readonly number[] {
    return this.#data.days;
  }

  /** 学習した日数の累計（減らない） */
  get dayCount(): number {
    return this.#data.nd;
  }

  get rts(): readonly number[] {
    return this.#data.rt;
  }

  rec(id: string): Rec | undefined {
    return this.#data.r[id];
  }

  /** 記録されている件数（未知の id も含む） */
  get size(): number {
    return Object.keys(this.#data.r).length;
  }

  /** 日付が変わっていたら今日のカウンタを 0 に戻す */
  #rollDay(): ProgressData['today'] {
    const today = this.#today();
    if (this.#data.today.d !== today) {
      this.#data.today = {d: today, n: 0, c: 0};
    }
    return this.#data.today;
  }

  /**
   * 回答を 1 件記録する。timedRt は計測できた正解の反応時間（不正解・計測不能なら省略）。
   */
  recordAnswer(
    id: string,
    outcome: Outcome,
    timedRt?: number,
    /**
     * scoreBox: false は「回答として数えるが、字の強さは動かさない」。読みから字を選ぶ向きの
     * 問題は通常向きと同じ尺度（反応時間の閾値）で測れないので、回答数・誤答数だけを積む。
     * 箱・最終回答日・学習日数はそのまま。箱だけ残して日を打ち直すと減衰が巻き戻って昇格に
     * なり、学習日を積むと箱 5・学習日 1 の字が反転の誤答で「定着」に化け、日だけ打ち直すと
     * その日の通常向きの正解で学習日が増えなくなる（定着に届かない）
     */
    opts?: {scoreBox?: boolean}
  ): RecordResult {
    const today = this.#today();
    const before = this.#data.r[id];
    const after = nextRec(before, outcome, today);
    if (opts?.scoreBox === false && before) {
      after[0] = before[0];
      after[1] = before[1];
      after[4] = before[4];
    }
    this.#data.r[id] = after;
    this.#dirtyIds.add(id);

    const t = this.#rollDay();
    const wasBelowGoal = t.n < this.#data.goal;
    t.n++;
    if (outcome !== 'wrong') t.c++;
    const goalReached = wasBelowGoal && t.n >= this.#data.goal;

    if (this.#data.days[this.#data.days.length - 1] !== today) {
      this.#data.days.push(today);
      if (this.#data.days.length > DAYS_KEPT) this.#data.days.splice(0, this.#data.days.length - DAYS_KEPT);
      this.#data.nd++;
    }
    if (timedRt !== undefined && (outcome === 'fast' || outcome === 'slow')) {
      this.#data.rt.push(Math.round(timedRt));
      if (this.#data.rt.length > RT_WINDOW) this.#data.rt.splice(0, this.#data.rt.length - RT_WINDOW);
    }

    this.#schedule();
    return {before, after, goalReached};
  }

  setGoal(goal: Goal) {
    if (!GOALS.includes(goal) || this.#data.goal === goal) return;
    this.#data.goal = goal;
    this.#touched.goal = true;
    this.flush(true);
  }

  /**
   * 文字種・文字数・反転を保存する。空の選択は sel を更新しない（直前の有効な選択が残る。
   * 全部外して離れた人を、次回また空の箱で迎えない）。
   * 目標と同じく設定なので即時に書く（回答のスロットルに乗せない。切り替えは
   * 離脱直前の最後の操作になりやすい）
   */
  setSettings(sel: readonly SettingKey[], cnt: number, reverse = true) {
    const next: QuizSettings = {...(this.#data.set ?? {}), ...(sanitizeSettings({sel, cnt}) ?? {})};
    // 反転は既定（混ぜる）を書かない。戻したら項目ごと消す
    if (reverse) delete next.rev;
    else next.rev = false;
    if (next.sel === undefined && next.cnt === undefined && next.rev === undefined) return;
    if (sameSettings(this.#data.set, next)) return;
    this.#data.set = next;
    this.#touched.set = true;
    this.flush(true);
  }

  /**
   * 「このブラウザに保存する」の切り替え。切ると記録本体をストレージから消し、
   * 切った印だけを残す（印を残さないと次回開いたときにまた保存が始まる）。
   * メモリ上の記録はそのセッションの間は使い続ける。
   */
  setEnabled(enabled: boolean) {
    if (this.enabled === enabled) return;
    this.#data.off = !enabled;
    this.#touched.off = true;
    this.flush(true);
  }

  /** 保留中の書き込みを今すぐ行う。force は差分が無くても直列化し直す */
  flush(force = false) {
    clearTimeout(this.#timer);
    this.#timer = undefined;
    if (!this.#dirty && !force) return;
    this.#dirty = false;
    this.#write();
  }

  /**
   * 記録をすべて消す。目標と保存設定は残す。直前の直列化結果を取り消し用に保持する。
   *
   * 消去の世代を進めて書くので、別のタブ・2 個目の要素は次の取り込みで自分の記録を捨てる。
   * 容量超過で書き込みを諦めていた場合も、removeItem は通るのでストレージからは消せる。
   * 未知の新しい版で読み取り専用のときはストレージに触らない（persisted = false）。
   */
  clear(): {count: number; persisted: boolean} {
    const count = this.size;
    this.#undo = JSON.stringify(this.#data);
    const fresh = emptyData(this.#today());
    fresh.goal = this.#data.goal;
    fresh.off = this.#data.off;
    fresh.set = this.#data.set;
    fresh.cl = this.#data.cl + 1;
    this.#data = fresh;
    this.#dirtyIds.clear();

    let persisted = false;
    if (this.#storage && !this.#readOnly) {
      try {
        this.#storage.removeItem(STORAGE_KEY);
        this.#lastWritten = null;
        this.#writable = true;
        persisted = true;
      } catch {
        // 触れない環境。メモリだけ消して続ける
      }
    }
    this.flush(true);
    return {count, persisted};
  }

  /**
   * 直前の clear() を取り消す。取り消せたら true。
   * 戻すのは記録本体だけで、消去のあとに変えた目標と「保存する」の設定はそのまま残す
   * （clear() が引き継いだものを undo が巻き戻してはいけない。「元に戻す」と
   * 「保存する」のチェックは同じパネルに並んでいる）。
   * 取り消しも「新しい操作」なので世代を進め、消去を取り込んだ相手に勝たせる。
   */
  undoClear(): boolean {
    if (this.#undo === null) return false;
    const {data} = sanitize(this.#undo, this.#today());
    this.#undo = null;
    data.goal = this.#data.goal;
    data.off = this.#data.off;
    data.set = this.#data.set;
    data.cl = this.#data.cl + 1;
    this.#data = data;
    this.flush(true);
    return true;
  }

  /**
   * ストレージに別のタブ・別の要素が書いた値があれば取り込む（書きはしない）。
   * 要素側が window の storage イベントで呼ぶ。
   */
  resync() {
    this.#absorbExternal();
  }

  /**
   * 自分が最後に書いた（読んだ）文字列と違う値がストレージにあれば、外部の書き込みとみなして
   * 取り込む。未知の新しい版なら以後読みも書きもしない。キーごと消されていたら
   * （埋め込み先が localStorage.clear() した等）記録を捨て、書き戻さない。
   */
  #absorbExternal() {
    if (!this.#storage || this.#readOnly) return;
    let raw: string | null;
    try {
      raw = this.#storage.getItem(STORAGE_KEY);
    } catch {
      return;
    }
    if (raw === this.#lastWritten) return;
    if (raw === null) {
      const fresh = emptyData(this.#today());
      fresh.goal = this.#data.goal;
      fresh.off = this.#data.off;
      fresh.set = this.#data.set;
      fresh.cl = this.#data.cl;
      this.#data = fresh;
      this.#lastWritten = null;
      return;
    }
    const {data, readOnly} = sanitize(raw, this.#today());
    if (readOnly) {
      this.#readOnly = true;
      this.#writable = false;
      return;
    }
    this.#data = mergeData(this.#data, data, this.#touched, this.#dirtyIds);
    this.#lastWritten = raw;
  }

  /**
   * 「自分で触った」の印は、その変更を載せた書き込みが通るまでで役目を終える。
   * 書けたあとはストレージの値が自分の値なので、以後は現在値に従ってよい。
   * 残しておくと、この要素が答えるたびの書き込みで、相手があとから変えた設定を
   * いつまでも自分の古い値で上書きし続ける（別のタブで文字種を変えても戻される）
   */
  #settleTouched() {
    this.#touched = {off: false, goal: false, set: false};
  }

  /** 取り消しの猶予が過ぎたら呼ぶ */
  forgetUndo() {
    this.#undo = null;
  }

  dispose() {
    this.flush();
  }

  #schedule() {
    this.#dirty = true;
    if (this.#timer !== undefined) return;
    this.#timer = setTimeout(() => {
      this.#timer = undefined;
      this.flush();
    }, WRITE_DELAY_MS);
  }

  #serialize(data: ProgressData = this.#data): string {
    // 未保存の設定はキーごと出さない（設定を触っていない利用者のペイロードを変えない）
    const set = data.set ? {set: data.set} : {};
    if (data.off) {
      // 印には学習の記録を入れない。設定（目標・文字種・文字数）は記録ではないので残す
      return JSON.stringify({v: SCHEMA_VERSION, off: true, goal: data.goal, ...set, cl: data.cl});
    }
    return JSON.stringify(data, (key, value) => (key === 'set' && value === null ? undefined : value));
  }

  #write() {
    if (!this.#writable || !this.#storage) return;
    // 書く直前に相手の書き込みを取り込む。全体置換なので、これが無いと相手の回答を消す
    this.#absorbExternal();
    if (!this.#writable) return;
    const s = this.#serialize();
    if (s === this.#lastWritten) return;
    try {
      this.#storage.setItem(STORAGE_KEY, s);
      this.#lastWritten = s;
      this.#dirtyIds.clear();
      this.#settleTouched();
      return;
    } catch {
      // 容量超過とみなして 1 回だけ剪定して再試行する
    }
    // 剪定はコピーに対して行い、書けたときだけメモリに反映する。
    // 失敗しても削ったままにすると、原因が別のキーだった場合に何も節約できないまま
    // セッション中の出題対象と表示だけが欠ける
    const pruned = this.#pruned();
    const s2 = this.#serialize(pruned);
    try {
      this.#storage.setItem(STORAGE_KEY, s2);
      this.#data = pruned;
      this.#lastWritten = s2;
      this.#dirtyIds.clear();
      this.#settleTouched();
    } catch {
      // これ以上は粘らない。以後 setItem を呼ばず、メモリ上の記録で出題だけ続ける
      this.#writable = false;
    }
  }

  /** 容量超過時の剪定。日付列と RT を詰め、長く触っていない定着レコードを古い順に落とす */
  #pruned(): ProgressData {
    const today = this.#today();
    const r = {...this.#data.r};
    const stale = Object.entries(r)
      .filter(([, rec]) => rec[0] === 5 && today - rec[1] >= PRUNE_AGE_DAYS)
      .sort((a, b) => a[1][1] - b[1][1])
      .slice(0, PRUNE_MAX);
    for (const [id] of stale) delete r[id];
    return {...this.#data, days: this.#data.days.slice(-7), rt: this.#data.rt.slice(-16), r};
  }
}
